import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fetchBlob, getToken } from "../api/client";
import type { PassportDetail, TimelineCase, TimelineDay } from "../api/types";
import { useEventSource } from "../hooks/useEventSource";
import { formatClock, formatDisplay, formatHourTick, todayISODate } from "../lib/dates";
import { AppleWalletMark } from "../components/AppleWalletMark";
import { PassportDrawer, confirmDeletePassport, scheduleSaveBody } from "../components/PassportDrawer";
import { useAuth } from "../auth/AuthContext";

const PX_PER_MIN = 1.35;
const ROW_H = 52;
const RULER_H = 32;
const LABEL_W = 248;

function statusCode(status: string): string {
  if (status === "incision_verified") return "INC";
  if (status === "preop_verified") return "WAIT";
  if (status === "locked") return "PRE";
  return "HLD";
}

function statusClass(status: string): string {
  if (status === "incision_verified") return "incision";
  if (status === "preop_verified") return "waiting";
  if (status === "locked") return "preop";
  return "hold";
}

/** Stable board color from OR suite number (OR-1 → room-1). */
function roomClass(suite?: string | null): string {
  const m = (suite || "").match(/(\d+)/);
  const n = m ? Number(m[1]) : 0;
  if (n >= 1 && n <= 8) return `room-${n}`;
  return "room-0";
}

function laneStart(c: TimelineCase): string | null {
  return c.lane_start_at || c.locked_at || c.scheduled_or_at || null;
}

function laneEnd(c: TimelineCase, nowIso: string): string | null {
  if (c.lane_end_at) return c.lane_end_at;
  if (c.incision_verified_at) return c.incision_verified_at;
  if (c.locked_at || c.preop_verified_at) return nowIso;
  return c.scheduled_or_at || null;
}

function buildHourTicks(winStartMs: number, winEndMs: number): Date[] {
  const ticks: Date[] = [];
  const first = new Date(winStartMs);
  first.setMinutes(0, 0, 0);
  if (first.getTime() < winStartMs) first.setHours(first.getHours() + 1);
  for (let t = first.getTime(); t <= winEndMs; t += 60 * 60 * 1000) {
    ticks.push(new Date(t));
  }
  return ticks;
}

export function TimelinePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [day, setDay] = useState(todayISODate);
  const [timeline, setTimeline] = useState<TimelineDay | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [error, setError] = useState("");
  const [detail, setDetail] = useState<PassportDetail | null>(null);
  const [printBusy, setPrintBusy] = useState("");
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const token = getToken();
  const streamUrl = token
    ? `/api/portal/timeline/stream?token=${encodeURIComponent(token)}&day=${encodeURIComponent(day)}`
    : null;

  const load = useCallback(async () => {
    const data = await api<TimelineDay>(`/api/portal/timeline?day=${encodeURIComponent(day)}`);
    setTimeline(data);
    setNowMs(Date.now());
    setError("");
  }, [day]);

  useEffect(() => {
    load().catch((e) => setError(e.message));
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const live = useEventSource(streamUrl, "timeline", (payload) => {
    try {
      setTimeline(JSON.parse(payload) as TimelineDay);
      setNowMs(Date.now());
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  });

  const cases = useMemo(() => timeline?.cases || [], [timeline]);
  const nowIso = useMemo(() => new Date(nowMs).toISOString(), [nowMs]);
  const rooms = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of cases) {
      const label = c.or_suite || "Unassigned";
      if (!seen.has(label)) seen.set(label, roomClass(c.or_suite));
    }
    return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [cases]);

  const geometry = useMemo(() => {
    if (!timeline) return null;
    const winStart = new Date(timeline.window_start).getTime();
    const winEnd = new Date(timeline.window_end).getTime();
    const spanMin = Math.max(60, (winEnd - winStart) / 60_000);
    const widthPx = Math.max(720, spanMin * PX_PER_MIN);
    const xOf = (iso: string | null | undefined) => {
      if (!iso) return 0;
      const t = new Date(iso).getTime();
      return Math.max(0, Math.min(widthPx, ((t - winStart) / 60_000) * PX_PER_MIN));
    };
    return {
      winStart,
      winEnd,
      widthPx,
      xOf,
      ticks: buildHourTicks(winStart, winEnd),
      nowX: nowMs >= winStart && nowMs <= winEnd ? ((nowMs - winStart) / 60_000) * PX_PER_MIN : null,
    };
  }, [timeline, nowMs]);

  async function openPassport(id: number) {
    setDetail(await api<PassportDetail>(`/api/portal/passports/${id}`));
  }

  return (
    <>
      <div className="toolbar">
        <div>
          <span className={`live-dot${live ? " on" : ""}`} />
          <strong>{live ? "LIVE" : "…"}</strong>
          {" · OR day board · "}
          {formatDisplay(day + "T12:00:00", false)}
          {timeline?.holdup_count ? ` · ${timeline.holdup_count} holdup` : ""}
        </div>
        <div className="toolbar-actions">
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Timeline day" />
          <button type="button" className="tool-btn" onClick={() => load().catch((e) => setError(e.message))}>
            Refresh
          </button>
        </div>
      </div>
      {error && <div className="banner-error">{error}</div>}

      <div className="anes-legend" aria-label="Room colors">
        {rooms.map(([label, tone]) => (
          <span key={label} className={`anes-key room ${tone}`}>
            {label}
          </span>
        ))}
        <span className="anes-key sched">│ Sched</span>
        <span className="anes-key now">│ Now</span>
        <span className="anes-key status-note">Badge = phase</span>
      </div>

      <div className="anes-shell">
        {!timeline || !geometry ? (
          <div className="empty">Loading day board…</div>
        ) : !cases.length ? (
          <div className="empty">No scheduled cases for this day.</div>
        ) : (
          <div className="anes-scroll">
            <div className="anes-frame" style={{ ["--anes-label-w" as string]: `${LABEL_W}px` }}>
              <div className="anes-corner">
                <div className="anes-corner-title">Case</div>
                <div className="anes-corner-sub">Patient · suite</div>
              </div>
              <div className="anes-ruler-wrap">
                <div className="anes-ruler" style={{ width: geometry.widthPx, height: RULER_H }}>
                  {geometry.ticks.map((t) => (
                    <div
                      key={t.toISOString()}
                      className="anes-tick"
                      style={{ left: geometry.xOf(t.toISOString()) }}
                    >
                      <span>{formatHourTick(t)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="anes-ylabels">
                {cases.map((c) => (
                  <button
                    type="button"
                    key={`y-${c.id}`}
                    className="anes-ylabel"
                    style={{ height: ROW_H }}
                    onClick={() => openPassport(c.id)}
                  >
                    <div className="anes-y-top">
                      <span className={`anes-badge ${statusClass(c.status)}`}>{statusCode(c.status)}</span>
                      <span className={`anes-suite ${roomClass(c.or_suite)}`}>{c.or_suite || "—"}</span>
                    </div>
                    <div className="anes-y-name">
                      {c.patient_name}
                      <AppleWalletMark show={c.in_apple_wallet} />
                    </div>
                    <div className="anes-y-sub">
                      {c.patient_mrn ? `MRN ${c.patient_mrn}` : c.passport_code}
                      {" · "}
                      {formatClock(c.scheduled_or_at)}
                    </div>
                  </button>
                ))}
              </div>

              <div className="anes-grid-wrap">
                <div
                  className="anes-grid"
                  style={{ width: geometry.widthPx, height: cases.length * ROW_H }}
                >
                  {geometry.ticks.map((t) => (
                    <div
                      key={`g-${t.toISOString()}`}
                      className="anes-vline"
                      style={{ left: geometry.xOf(t.toISOString()) }}
                    />
                  ))}
                  {geometry.nowX != null && (
                    <div className="anes-now" style={{ left: geometry.nowX }} title="Now" />
                  )}
                  {cases.map((c, idx) => {
                    const start = laneStart(c);
                    const end = laneEnd(c, nowIso);
                    const x0 = geometry.xOf(start);
                    const x1 = Math.max(geometry.xOf(end), x0 + 6);
                    const schedX = c.scheduled_or_at ? geometry.xOf(c.scheduled_or_at) : null;
                    const top = idx * ROW_H;
                    return (
                      <div key={c.id} className="anes-row" style={{ top, height: ROW_H }}>
                        <div className="anes-row-rule" />
                        <button
                          type="button"
                          className={`anes-track ${roomClass(c.or_suite)}${c.is_holdup ? " holdup" : ""}`}
                          style={{ left: x0, width: x1 - x0 }}
                          onClick={() => openPassport(c.id)}
                          title={`${c.or_suite || "No suite"} · ${c.procedure} · ${formatClock(start)} → ${formatClock(end)}`}
                        >
                          <span className="anes-track-label">{c.procedure}</span>
                          {c.laterality ? <span className="anes-track-lat">{c.laterality}</span> : null}
                        </button>
                        {schedX != null && (
                          <div
                            className="anes-sched"
                            style={{ left: schedX }}
                            title={`Scheduled ${formatClock(c.scheduled_or_at)}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {detail && (
        <PassportDrawer
          detail={detail}
          printBusy={printBusy}
          archiveBusy={archiveBusy}
          defaultSuite={detail.or_suite || ""}
          defaultSched={detail.scheduled_or_at || ""}
          defaultFacility={detail.facility || ""}
          defaultArrive={detail.facility_at || ""}
          onClose={() => setDetail(null)}
          onSave={async (id, fields) => {
            setDetail(
              await api(`/api/portal/passports/${id}/suite`, {
                method: "PATCH",
                body: JSON.stringify(scheduleSaveBody(fields)),
              }),
            );
            await load();
          }}
          onPrint={async (kind) => {
            setPrintBusy(kind);
            try {
              const path =
                kind === "consent"
                  ? `/api/passports/${detail.id}/consent/pdf`
                  : kind === "report"
                    ? `/api/passports/${detail.id}/report/pdf`
                    : `/api/passports/${detail.id}/record/pdf`;
              const meta = await api<{ download_url: string }>(path, { method: "POST" });
              const blob = await fetchBlob(meta.download_url);
              const url = URL.createObjectURL(blob);
              window.open(url, "_blank", "noopener,noreferrer");
            } finally {
              setPrintBusy("");
            }
          }}
          onArchive={async () => {
            setArchiveBusy(true);
            try {
              const data = await api<PassportDetail>(`/api/portal/passports/${detail.id}/complete`, {
                method: "POST",
              });
              setDetail(data);
              await load();
            } finally {
              setArchiveBusy(false);
            }
          }}
          onDelete={
            isAdmin
              ? async () => {
                  setDeleteBusy(true);
                  try {
                    if (await confirmDeletePassport(detail.passport_code, detail.id)) {
                      setDetail(null);
                      await load();
                    }
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  } finally {
                    setDeleteBusy(false);
                  }
                }
              : undefined
          }
          deleteBusy={deleteBusy}
        />
      )}
    </>
  );
}
